/**
 * Pure H OS · Sidebar Navigation Configuration (Wave A · Amended IA)
 *
 * Single source of truth for the entire sidebar. The Sidebar component
 * renders from this config so that role-based filtering, favorites, recent
 * pages, and quick actions all operate on a stable structure.
 *
 * If `roles` is undefined on an item OR a domain, every authenticated user
 * sees it. Owners (`role: "owner"` OR `isHoldingOwner: true`) always see
 * everything regardless of `roles`.
 */
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard, BarChart3, FileText, Users, Cog, Bot, Bell,
  Building2, Compass, User as UserIcon, Megaphone, ShoppingCart, Factory,
  Wallet, FlaskConical, Rocket, Sparkles, Briefcase, TrendingUp, Scale,
  Target, Newspaper, Sun, Clock, GitBranch, ShoppingBag, FolderOpen, Flag,
  BookOpen, CalendarClock, ShieldAlert, Warehouse as WarehouseIcon, Coins,
  PieChart, UserSquare2, Package, HandCoins, LineChart, ClipboardList,
  Beaker, TestTube2, Eye, Layers, Truck, Boxes, Store, Receipt, Landmark,
  Presentation, ScrollText, Calendar as CalendarIcon, ShieldCheck, Gavel,
  MessageSquare, Building, Wrench,
} from "lucide-react";

export type PurehRole = "owner" | "admin" | "manager" | "employee";

export interface NavItem {
  key: string;               // stable identifier — used for favorites/recent
  to: string;                // route path
  icon: LucideIcon;
  label: string;
  testId: string;            // data-testid for the anchor
  roles?: PurehRole[];       // omit → everyone sees it
  ownerOnly?: boolean;       // true → only owner + isHoldingOwner see it
  locked?: boolean;          // true → grey out (Manufacturing / Shopify)
  badge?: string;            // e.g. "Locked" · rendered as pill
}

export interface NavDomain {
  key: string;
  label: string;
  items: NavItem[];
  roles?: PurehRole[];       // omit → everyone sees it
  ownerOnly?: boolean;
}

// ─────────────────────────────────────────────────────────────────────
// The amended 15-domain sidebar (Product Master is its own domain).
// ─────────────────────────────────────────────────────────────────────
export const NAV_DOMAINS: NavDomain[] = [
  {
    key: "executive",
    label: "Executive",
    items: [
      { key: "owner-cockpit",   to: "/pureh/owner",       icon: Sun,             label: "Owner Cockpit",       testId: "nav-owner",        ownerOnly: true },
      { key: "exec-dashboard",  to: "/pureh/dashboard",   icon: LayoutDashboard, label: "Executive Dashboard", testId: "nav-dashboard" },
      { key: "decisions",       to: "/pureh/decisions",   icon: Scale,           label: "Decisions",           testId: "nav-decisions",    roles: ["owner","admin","manager"] },
      { key: "board-briefs",    to: "/pureh/briefs",      icon: Newspaper,       label: "Board Briefs",        testId: "nav-briefs",       roles: ["owner","admin"] },
      { key: "scorecards",      to: "/pureh/scorecards",  icon: Target,          label: "Scorecards",          testId: "nav-scorecards",   roles: ["owner","admin","manager"] },
    ],
  },
  {
    key: "workspace",
    label: "Workspace",
    items: [
      { key: "my-day",          to: "/pureh/workspace",     icon: Briefcase,     label: "My Workspace",        testId: "nav-workspace" },
      { key: "notifications",   to: "/pureh/notifications", icon: Bell,          label: "Notifications",       testId: "nav-notifications" },
      { key: "approvals",       to: "/pureh/approvals",     icon: FileText,      label: "Approvals",           testId: "nav-approvals" },
      { key: "exec-inbox",      to: "/pureh/workspace/inbox", icon: MessageSquare, label: "Inbox",           testId: "nav-inbox",        roles: ["owner","admin","manager","employee"] },
    ],
  },
  {
    // Kept as standalone top-level per owner amendment.
    key: "product-master",
    label: "Product Master",
    items: [
      { key: "product-master",  to: "/pureh/product-master", icon: Package,      label: "Product Master",      testId: "nav-product-master" },
      { key: "products-hub",    to: "/pureh/products-hub",   icon: Layers,       label: "Product Workspace",   testId: "nav-products-hub",  roles: ["owner","admin","manager"] },
    ],
  },
  {
    key: "rd",
    label: "R&D",
    items: [
      { key: "rd-laboratory",   to: "/pureh/rd-lab",            icon: FlaskConical,  label: "R&D Laboratory",   testId: "nav-rd-laboratory" },
      { key: "raw-materials",   to: "/pureh/raw-materials",     icon: TestTube2,     label: "Raw Materials",    testId: "nav-raw-materials" },
      { key: "blend-builder",   to: "/pureh/blend",             icon: Sparkles,      label: "Blend Builder",    testId: "nav-blend-builder" },
      { key: "rd-suppliers",    to: "/pureh/rd/suppliers",      icon: Truck,         label: "Suppliers",        testId: "nav-rd-suppliers" },
      // Legacy "R&D Operating System" surfaces (Formula Manager, Formula
      // Bank, Lab Sessions, Sampling, Blind Testing, Decisions, Timeline)
      // retired from nav 2026-07-21 — demo-store viewers, NOT bound to
      // Product Master. Canonical formula work happens in R&D Laboratory.
    ],
    roles: ["owner","admin","manager","employee"],
  },
  {
    key: "operations",
    label: "Operations",
    items: [
      { key: "ops-dashboard",   to: "/pureh/operations",              icon: LayoutDashboard, label: "Operations Dashboard", testId: "nav-ops-dashboard" },
      { key: "inventory",       to: "/pureh/inventory",               icon: WarehouseIcon,   label: "Inventory",            testId: "nav-inventory" },
      { key: "manufacturing",   to: "/pureh/operations/batches",      icon: Factory,        label: "Manufacturing",         testId: "nav-manufacturing", locked: true, badge: "Locked" },
      { key: "quality",         to: "/pureh/operations/quality",      icon: ShieldCheck,     label: "Quality",              testId: "nav-quality" },
      { key: "ops-suppliers",   to: "/pureh/operations/suppliers",    icon: Truck,           label: "Suppliers",            testId: "nav-ops-suppliers" },
      { key: "procurement",     to: "/pureh/procurement",             icon: ShoppingBag,     label: "Procurement",          testId: "nav-procurement" },
      { key: "fulfillment",     to: "/pureh/operations/fulfillment",  icon: Boxes,           label: "Fulfillment",          testId: "nav-fulfillment" },
    ],
    roles: ["owner","admin","manager","employee"],
  },
  {
    key: "commerce",
    label: "Commerce",
    items: [
      { key: "commerce-overview", to: "/pureh/commerce",           icon: LayoutDashboard, label: "Overview",            testId: "nav-commerce-overview" },
      { key: "orders",            to: "/pureh/commerce/orders",    icon: ShoppingCart,    label: "Orders",              testId: "nav-orders" },
      { key: "catalogue",         to: "/pureh/commerce/products",  icon: Store,           label: "Catalogue",           testId: "nav-catalogue" },
      { key: "pricing",           to: "/pureh/commerce/pricing",   icon: Coins,           label: "Pricing & Promos",    testId: "nav-pricing" },
      { key: "shopify",           to: "/pureh/commerce/sync",      icon: Store,           label: "Shopify Sync",        testId: "nav-shopify", locked: true, badge: "Locked" },
      { key: "signals",           to: "/pureh/commerce/signals",   icon: TrendingUp,      label: "Signals",             testId: "nav-signals" },
    ],
    roles: ["owner","admin","manager"],
  },
  {
    key: "finance",
    label: "Finance",
    items: [
      { key: "finance-overview",  to: "/pureh/finance",                     icon: LayoutDashboard, label: "Overview",              testId: "nav-finance-overview" },
      { key: "finance-ar",        to: "/pureh/finance/invoices",            icon: Receipt,         label: "Accounts Receivable",   testId: "nav-finance-ar" },
      { key: "finance-soa",       to: "/pureh/finance/customer-statements", icon: ScrollText,      label: "Customer Statements",   testId: "nav-finance-soa" },
      { key: "finance-ap",        to: "/pureh/finance/expenses",            icon: FileText,        label: "Accounts Payable",      testId: "nav-finance-ap" },
      { key: "finance-payments",  to: "/pureh/finance/business-reality",    icon: HandCoins,       label: "Payments",              testId: "nav-finance-payments" },
      { key: "finance-cashflow",  to: "/pureh/finance/cash",                icon: LineChart,       label: "Cash Flow",             testId: "nav-finance-cashflow" },
      { key: "finance-budget",    to: "/pureh/finance/budget",              icon: Target,          label: "Budget",                testId: "nav-finance-budget" },
      { key: "finance-pnl",       to: "/pureh/finance/pnl",                 icon: BarChart3,       label: "P&L",                   testId: "nav-finance-pnl" },
      { key: "finance-taxes",     to: "/pureh/finance/taxes",               icon: Gavel,           label: "Taxes",                 testId: "nav-finance-taxes" },
      { key: "finance-cc",        to: "/pureh/cost-centers",                icon: PieChart,        label: "Cost Centers",          testId: "nav-cost-centers" },
      { key: "finance-reports",   to: "/pureh/finance/reports",             icon: ScrollText,      label: "Reports",               testId: "nav-finance-reports" },
    ],
    roles: ["owner","admin","manager"],
  },
  {
    key: "growth",
    label: "Growth",
    items: [
      { key: "growth-overview",   to: "/pureh/growth",              icon: TrendingUp,   label: "Overview",           testId: "nav-growth-overview" },
      { key: "customer-360",      to: "/pureh/customers",           icon: UserSquare2,  label: "Customers (360)",    testId: "nav-customer-360" },
      { key: "sales-pipeline",    to: "/pureh/growth/sales",        icon: BarChart3,    label: "Sales Pipeline",     testId: "nav-sales-pipeline" },
      { key: "retention",         to: "/pureh/growth/retention",    icon: TrendingUp,   label: "Retention & Loyalty",testId: "nav-retention" },
      { key: "attribution",       to: "/pureh/growth/attribution",  icon: PieChart,     label: "Attribution",        testId: "nav-attribution" },
    ],
    roles: ["owner","admin","manager"],
  },
  {
    key: "marketing",
    label: "Marketing",
    items: [
      { key: "mk-overview",       to: "/pureh/marketing",            icon: Megaphone,       label: "Overview",         testId: "nav-mk-overview" },
      { key: "mk-calendar",       to: "/pureh/marketing/calendar",   icon: CalendarIcon,    label: "Calendar",         testId: "nav-mk-calendar" },
      { key: "mk-pipeline",       to: "/pureh/marketing/pipeline",   icon: GitBranch,       label: "Pipeline",         testId: "nav-mk-pipeline" },
      { key: "mk-creative",       to: "/pureh/marketing/creative",   icon: Sparkles,        label: "Creative Review",  testId: "nav-mk-creative" },
      { key: "mk-copy",           to: "/pureh/marketing/copy",       icon: FileText,        label: "Copy Assistant",   testId: "nav-mk-copy" },
      { key: "mk-creators",       to: "/pureh/marketing/creators",   icon: Users,           label: "Creators",         testId: "nav-mk-creators" },
      { key: "mk-channels",       to: "/pureh/marketing/channels",   icon: Compass,         label: "Channels",         testId: "nav-mk-channels" },
    ],
    roles: ["owner","admin","manager"],
  },
  {
    key: "launch",
    label: "Launch",
    items: [
      { key: "launch-overview",   to: "/pureh/launch",              icon: Rocket,       label: "Overview",       testId: "nav-launch-overview" },
      { key: "launch-control",    to: "/pureh/launch/control",      icon: Rocket,       label: "Launch Control", testId: "nav-launch-control" },
      { key: "launch-checklist",  to: "/pureh/launch/checklist",    icon: ClipboardList,label: "Checklist",      testId: "nav-launch-checklist" },
      { key: "launch-preorders",  to: "/pureh/launch/preorders",    icon: ShoppingBag,  label: "Pre-Orders",     testId: "nav-launch-preorders" },
      { key: "launch-subs",       to: "/pureh/launch/subscribers",  icon: Bell,         label: "Subscribers",    testId: "nav-launch-subs" },
    ],
    roles: ["owner","admin","manager"],
  },
  {
    key: "investors",
    label: "Investors",
    items: [
      { key: "investor-ws",       to: "/pureh/investors",  icon: Briefcase, label: "Investor Workspace", testId: "nav-investors" },
    ],
    ownerOnly: true,
  },
  {
    key: "people",
    label: "People",
    items: [
      { key: "team",              to: "/pureh/workspace/people", icon: Users,        label: "Team",         testId: "nav-team",  roles: ["owner","admin","manager"] },
      { key: "departments",       to: "/pureh/departments",      icon: Building,     label: "Departments",  testId: "nav-dept",  roles: ["owner","admin","manager"] },
    ],
    roles: ["owner","admin","manager"],
  },
  {
    key: "knowledge",
    label: "Knowledge",
    items: [
      { key: "documents",         to: "/pureh/documents",        icon: FolderOpen,   label: "Documents",           testId: "nav-documents" },
      { key: "document-center",   to: "/pureh/documents/center", icon: FolderOpen,   label: "Document Center",     testId: "nav-document-center" },
      { key: "kb",                to: "/pureh/knowledge",        icon: BookOpen,     label: "Knowledge Base",      testId: "nav-knowledge" },
      { key: "reports",           to: "/pureh/reports",          icon: BarChart3,    label: "Reports",             testId: "nav-reports" },
      { key: "analytics",         to: "/pureh/analytics",        icon: LineChart,    label: "Analytics Catalogue", testId: "nav-analytics" },
      { key: "meetings",          to: "/pureh/meetings",         icon: CalendarClock,label: "Meetings",            testId: "nav-meetings" },
      { key: "licensing",         to: "/pureh/licensing",        icon: ShieldCheck,  label: "Licensing",           testId: "nav-licensing" },
    ],
  },
  {
    key: "governance",
    label: "Governance",
    items: [
      { key: "timeline",          to: "/pureh/timeline", icon: Clock,        label: "Timeline",         testId: "nav-timeline" },
      { key: "pmo",               to: "/pureh/pmo",      icon: GitBranch,    label: "PMO",              testId: "nav-pmo",   roles: ["owner","admin","manager"] },
      { key: "okrs",              to: "/pureh/okrs",     icon: Flag,         label: "OKRs",             testId: "nav-okrs",  roles: ["owner","admin","manager"] },
      { key: "risks",             to: "/pureh/risks",    icon: ShieldAlert,  label: "Risk Register",    testId: "nav-risks", roles: ["owner","admin"] },
      { key: "presentation",      to: "/pureh/presentation", icon: Presentation, label: "Presentation Mode", testId: "nav-presentation", ownerOnly: true },
    ],
    roles: ["owner","admin","manager","employee"],
  },
  {
    key: "ai",
    label: "AI",
    items: [
      { key: "ask-orvan",         to: "/pureh/ai/conversations",  icon: Bot,       label: "Ask ORVAN",    testId: "nav-ask-orvan" },
    ],
  },
  {
    key: "administration",
    label: "Administration",
    items: [
      { key: "company-setup",     to: "/pureh/company-setup",  icon: Building2,    label: "Company Setup",        testId: "nav-company-setup",  roles: ["owner","admin"] },
      { key: "users-access",      to: "/pureh/admin/users",    icon: UserSquare2,  label: "Users & Access",       testId: "nav-users-access",   roles: ["owner","admin"] },
      { key: "ai-hq",             to: "/pureh/ai",             icon: Sparkles,     label: "AI HQ",                testId: "nav-ai-hq",          roles: ["owner","admin"] },
      { key: "notifications-hist",to: "/pureh/notifications",  icon: Bell,         label: "Notification Center",  testId: "nav-notif-center",   roles: ["owner","admin"] },
      { key: "settings",          to: "/pureh/settings",       icon: Cog,          label: "Settings",             testId: "nav-settings" },
    ],
    // Domain-level gate removed — Settings must be reachable by every role.
    // Individual admin-only items still enforce their own `roles` gate.
  },
];

// Flatten helper — used for Favorites, Recent Pages, and Quick Actions.
export const ALL_NAV_ITEMS: NavItem[] = NAV_DOMAINS.flatMap((d) => d.items);
export const NAV_ITEM_BY_KEY: Record<string, NavItem> =
  Object.fromEntries(ALL_NAV_ITEMS.map((it) => [it.key, it]));
export const NAV_ITEM_BY_PATH: Record<string, NavItem> =
  Object.fromEntries(ALL_NAV_ITEMS.map((it) => [it.to, it]));

// ─────────────────────────────────────────────────────────────────────
// Role gating helpers
// ─────────────────────────────────────────────────────────────────────
export interface RoleContext {
  role: PurehRole | string;
  isHoldingOwner: boolean;
}

export function isOwner(ctx: RoleContext): boolean {
  return (ctx.role || "").toLowerCase() === "owner" || ctx.isHoldingOwner === true;
}

export function canSeeItem(item: NavItem, ctx: RoleContext): boolean {
  if (isOwner(ctx)) return true;
  if (item.ownerOnly) return false;
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.includes(((ctx.role || "").toLowerCase() as PurehRole));
}

export function canSeeDomain(domain: NavDomain, ctx: RoleContext): boolean {
  if (isOwner(ctx)) return true;
  if (domain.ownerOnly) return false;
  if (domain.roles && !domain.roles.includes(((ctx.role || "").toLowerCase() as PurehRole))) return false;
  // Also require at least one visible child.
  return domain.items.some((it) => canSeeItem(it, ctx));
}

// ─────────────────────────────────────────────────────────────────────
// Quick Actions catalog · surfaces via ⌘K palette AND sidebar strip.
// Keep intentionally small · every action is high-value & role-scoped.
// ─────────────────────────────────────────────────────────────────────
export interface QuickAction {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  testId: string;
  roles?: PurehRole[];
  ownerOnly?: boolean;
}

export const QUICK_ACTIONS: QuickAction[] = [
  { key: "qa-new-payment",   label: "New payment",       to: "/pureh/finance/business-reality?new=payment", icon: HandCoins, testId: "qa-new-payment",   roles: ["owner","admin","manager"] },
  { key: "qa-new-cod",       label: "New COD batch",     to: "/pureh/finance/business-reality?new=cod",     icon: Truck,     testId: "qa-new-cod",       roles: ["owner","admin","manager"] },
  { key: "qa-new-grn",       label: "New GRN",           to: "/pureh/inventory/grn?new=1",                  icon: Boxes,     testId: "qa-new-grn",       roles: ["owner","admin","manager","employee"] },
  { key: "qa-new-product",   label: "New product",       to: "/pureh/product-master?new=1",                 icon: Package,   testId: "qa-new-product",   roles: ["owner","admin","manager"] },
  { key: "qa-new-decision",  label: "Log a decision",    to: "/pureh/decisions?new=1",                      icon: Scale,     testId: "qa-new-decision",  roles: ["owner","admin","manager"] },
  { key: "qa-owner-brief",   label: "Open Owner Cockpit",to: "/pureh/owner",                                icon: Sun,       testId: "qa-owner-brief",   ownerOnly: true },
];

export function visibleQuickActions(ctx: RoleContext): QuickAction[] {
  return QUICK_ACTIONS.filter((qa) => {
    if (isOwner(ctx)) return true;
    if (qa.ownerOnly) return false;
    if (!qa.roles || qa.roles.length === 0) return true;
    return qa.roles.includes(((ctx.role || "").toLowerCase() as PurehRole));
  });
}
